import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Account, CreateTransactionRequest, JournalEntryInput } from '../../../services/ledger-client/types';

interface OpeningBalanceRowValue {
  accountId: string;
  openingBalance: number;
  memo?: string;
}

export interface OpeningBalancesDialogData {
  accounts: Account[];
}

@Component({
  selector: 'app-opening-balances-dialog',
  templateUrl: './opening-balances-dialog.component.html',
  styleUrls: ['./opening-balances-dialog.component.scss'],
})
export class OpeningBalancesDialogComponent implements OnInit {
  form: UntypedFormGroup = this.formBuilder.group({
    source: ['finance-admin-opening-balance'],
    description: ['Opening balances'],
    externalReference: [''],
    postedAt: ['', Validators.required],
    offsetAccountId: [''],
    rows: this.formBuilder.array([]),
  });

  errorMessage = '';
  readonly sortedAccounts: Account[];
  private readonly accountById = new Map<string, Account>();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: OpeningBalancesDialogData,
    private dialogRef: MatDialogRef<OpeningBalancesDialogComponent, CreateTransactionRequest | null>,
    private formBuilder: UntypedFormBuilder,
  ) {
    this.sortedAccounts = [...(data?.accounts || [])].sort((a, b) => {
      const aValue = `${a.code || ''} ${a.name || a.id}`.toLowerCase();
      const bValue = `${b.code || ''} ${b.name || b.id}`.toLowerCase();
      if (aValue > bValue) {
        return 1;
      }
      if (aValue < bValue) {
        return -1;
      }
      return 0;
    });

    this.sortedAccounts.forEach((account) => this.accountById.set(account.id, account));
  }

  ngOnInit(): void {
    this.form.get('postedAt')?.setValue(new Date().toISOString().slice(0, 10));
    this.addRow();
  }

  get rows(): UntypedFormArray {
    return this.form.get('rows') as UntypedFormArray;
  }

  get rowGroups(): UntypedFormGroup[] {
    return this.rows.controls as UntypedFormGroup[];
  }

  get canSubmit(): boolean {
    return this.sortedAccounts.length > 0;
  }

  addRow(): void {
    this.rows.push(this.formBuilder.group({
      accountId: ['', Validators.required],
      openingBalance: [null, Validators.required],
      memo: [''],
    }));
  }

  removeRow(index: number): void {
    if (this.rows.length <= 1) {
      return;
    }
    this.rows.removeAt(index);
  }

  save(): void {
    this.errorMessage = '';
    this.form.markAllAsTouched();

    if (!this.canSubmit) {
      this.errorMessage = 'No accounts are available yet.';
      return;
    }

    if (this.form.invalid) {
      this.errorMessage = 'Please complete required fields.';
      return;
    }

    const rows = this.parseRows();
    if (!rows.length) {
      this.errorMessage = 'Add at least one non-zero opening balance.';
      return;
    }

    const openingEntries: JournalEntryInput[] = [];
    rows.forEach((row) => {
      const account = this.accountById.get(row.accountId);
      if (!account) {
        return;
      }
      const entry = this.toOpeningEntry(account, row.openingBalance, row.memo);
      if (entry) {
        openingEntries.push(entry);
      }
    });

    if (!openingEntries.length) {
      this.errorMessage = 'No valid opening balances were provided.';
      return;
    }

    const totalDebits = this.roundAmount(openingEntries.reduce((sum, entry) => sum + Number(entry.debitAmount || 0), 0));
    const totalCredits = this.roundAmount(openingEntries.reduce((sum, entry) => sum + Number(entry.creditAmount || 0), 0));
    const difference = this.roundAmount(totalDebits - totalCredits);

    const entries: JournalEntryInput[] = [...openingEntries];
    if (Math.abs(difference) >= 0.005) {
      const offsetAccountId = String(this.form.get('offsetAccountId')?.value || '').trim();
      if (!offsetAccountId) {
        this.errorMessage = 'Offset account is required when opening balances are not naturally balanced.';
        return;
      }

      entries.push({
        accountId: offsetAccountId,
        debitAmount: difference < 0 ? this.roundAmount(Math.abs(difference)) : undefined,
        creditAmount: difference > 0 ? this.roundAmount(difference) : undefined,
        memo: 'Opening balance offset',
      });
    }

    const sourceValue = String(this.form.get('source')?.value || '').trim();
    const descriptionValue = String(this.form.get('description')?.value || '').trim();
    const externalReferenceValue = String(this.form.get('externalReference')?.value || '').trim();
    const postedAtValue = String(this.form.get('postedAt')?.value || '').trim();

    const payload: CreateTransactionRequest = {
      source: sourceValue || undefined,
      description: descriptionValue || undefined,
      externalReference: externalReferenceValue || undefined,
      postedAt: new Date(postedAtValue).toISOString(),
      entries,
    };

    this.dialogRef.close(payload);
  }

  cancel(): void {
    this.dialogRef.close(null);
  }

  accountLabel(account: Account): string {
    const code = account.code ? `${account.code} - ` : '';
    return `${code}${account.name}`;
  }

  private parseRows(): OpeningBalanceRowValue[] {
    return this.rowGroups
      .map((row) => ({
        accountId: String(row.get('accountId')?.value || '').trim(),
        openingBalance: this.roundAmount(Number(row.get('openingBalance')?.value)),
        memo: String(row.get('memo')?.value || '').trim() || undefined,
      }))
      .filter((row) => row.accountId && Number.isFinite(row.openingBalance) && Math.abs(row.openingBalance) > 0);
  }

  private toOpeningEntry(account: Account, openingBalance: number, memo?: string): JournalEntryInput | null {
    const amount = this.roundAmount(Math.abs(openingBalance));
    if (amount <= 0) {
      return null;
    }

    const isDebitNormal = account.normalBalance === 'debit';
    const usesNormalSide = openingBalance > 0;
    const shouldDebit = usesNormalSide ? isDebitNormal : !isDebitNormal;

    return {
      accountId: account.id,
      debitAmount: shouldDebit ? amount : undefined,
      creditAmount: shouldDebit ? undefined : amount,
      memo,
    };
  }

  private roundAmount(value: number): number {
    return Math.round((value + Number.EPSILON) * 100) / 100;
  }
}
